import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import GestureIcon from "@mui/icons-material/Gesture";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";

// Internal drawing-buffer resolution — independent of however large the
// canvas is actually displayed (it's stretched to 100% width via CSS), so
// strokes stay crisp regardless of screen size.
const CANVAS_WIDTH = 500;
const CANVAS_HEIGHT = 200;

// Maps a pointer event to a coordinate in the canvas's internal pixel
// space, correcting for any CSS scaling between the canvas's actual
// rendered size and its drawing-buffer resolution.
const getPointFromEvent = (canvas, event) => {
  const rect = canvas.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) * canvas.width) / rect.width,
    y: ((event.clientY - rect.top) * canvas.height) / rect.height,
  };
};

// A live, drawn signature — mouse/touch/stylus, like signing on a delivery
// tablet. `value` is a PNG data URL (or "" if never signed); `onChange`
// fires with a new data URL once the user actually draws something and
// hits Save. Re-signing always starts from a blank canvas — the previous
// signature is only shown as the read-only trigger thumbnail below, never
// pre-loaded into the canvas, since importing an existing image back into
// a fresh drawing surface brings coordinate/scaling edge cases that aren't
// worth it for this feature. `accent` tints the trigger/dialog to match
// whichever form it's dropped into (e.g. Issue's green vs Receive's blue).
const SignaturePad = ({ value, onChange, accent = "#1976d2" }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const hasStrokeRef = useRef(false);

  // The canvas itself stays transparent — the dashed guide line and "×" are
  // a separate decorative layer underneath, purely visual, so they never
  // get baked into the exported signature image.
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    hasStrokeRef.current = false;
  };

  // Deferred a tick: the Dialog mounts its content via a transition, so the
  // <canvas> may not exist in the DOM yet on the same render the dialog
  // opens.
  useEffect(() => {
    if (open) {
      const timer = setTimeout(clearCanvas, 0);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const startDraw = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    canvas.setPointerCapture(event.pointerId);
    isDrawingRef.current = true;
    lastPointRef.current = getPointFromEvent(canvas, event);
  };

  const draw = (event) => {
    if (!isDrawingRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const point = getPointFromEvent(canvas, event);
    ctx.strokeStyle = "#1e293b";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    hasStrokeRef.current = true;
  };

  const stopDraw = () => {
    isDrawingRef.current = false;
    lastPointRef.current = null;
  };

  const handleSave = () => {
    // Nothing drawn — leave whatever signature (if any) already existed
    // alone rather than silently wiping it out.
    if (hasStrokeRef.current) {
      const canvas = canvasRef.current;
      // Composite onto an opaque white background at export time — the
      // canvas itself stayed transparent so the guide line underneath
      // never became part of the drawing.
      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = canvas.width;
      exportCanvas.height = canvas.height;
      const exportCtx = exportCanvas.getContext("2d");
      exportCtx.fillStyle = "#ffffff";
      exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
      exportCtx.drawImage(canvas, 0, 0);
      onChange(exportCanvas.toDataURL("image/png"));
    }
    setOpen(false);
  };

  return (
    <Box>
      {value ? (
        <Tooltip title={t("re_sign")}>
          <Box
            onClick={() => setOpen(true)}
            sx={{
              position: "relative",
              display: "inline-flex",
              cursor: "pointer",
              borderRadius: "10px",
              border: `1.5px solid ${accent}33`,
              padding: "4px 8px",
              backgroundColor: "#fff",
              transition: "box-shadow .18s ease, transform .18s ease",
              "&:hover": {
                boxShadow: `0 4px 14px ${accent}26`,
                transform: "translateY(-1px)",
              },
            }}
          >
            <img
              src={value}
              alt={t("signature")}
              style={{ height: "34px", display: "block" }}
            />
            <CheckCircleIcon
              sx={{
                position: "absolute",
                top: -6,
                right: -6,
                fontSize: "1.05em",
                color: "#22c55e",
                backgroundColor: "#fff",
                borderRadius: "50%",
              }}
            />
          </Box>
        </Tooltip>
      ) : (
        <Tooltip title={t("sign")}>
          <IconButton
            onClick={() => setOpen(true)}
            sx={{
              width: 44,
              height: 44,
              backgroundColor: `${accent}1f`,
              color: accent,
              border: `1.5px dashed ${accent}66`,
              transition: "transform .18s ease, background-color .18s ease",
              "&:hover": {
                backgroundColor: `${accent}33`,
                transform: "scale(1.06)",
              },
            }}
          >
            <GestureIcon />
          </IconButton>
        </Tooltip>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Zoom}
        transitionDuration={220}
        PaperProps={{
          sx: {
            borderRadius: "1.5em",
            overflow: "visible",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)",
          },
        }}
      >
        <IconButton
          onClick={() => setOpen(false)}
          sx={{
            position: "absolute",
            top: -14,
            right: -14,
            backgroundColor: "#fff",
            boxShadow: "0 4px 12px rgba(15, 23, 42, 0.18)",
            "&:hover": { backgroundColor: "#f8fafc" },
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: `${accent}1f`, color: accent, width: 40, height: 40 }}>
              <GestureIcon />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                {t("sign_here")}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              position: "relative",
              borderRadius: "1em",
              overflow: "hidden",
              background: "linear-gradient(180deg, #fbfcfe 0%, #f3f6fb 100%)",
              border: `1.5px solid ${accent}33`,
            }}
          >
            {/* Decorative "sign here" guide — purely visual, sits behind
                the transparent canvas and never gets exported. */}
            <Box
              sx={{
                position: "absolute",
                left: "6%",
                right: "6%",
                bottom: "20%",
                borderBottom: "2px dashed #cbd5e1",
              }}
            />
            <Typography
              sx={{
                position: "absolute",
                left: "5%",
                bottom: "calc(20% + 4px)",
                color: "#94a3b8",
                fontSize: "1.1em",
                fontWeight: 700,
              }}
            >
              ×
            </Typography>
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              style={{
                position: "relative",
                display: "block",
                width: "100%",
                height: `${CANVAS_HEIGHT}px`,
                touchAction: "none",
                cursor: "crosshair",
              }}
              onPointerDown={startDraw}
              onPointerMove={draw}
              onPointerUp={stopDraw}
              onPointerLeave={stopDraw}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Tooltip title={t("clear")}>
            <IconButton onClick={clearCanvas} sx={{ color: "text.secondary" }}>
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Box
            component="button"
            onClick={() => setOpen(false)}
            sx={{
              border: "none",
              background: "none",
              color: "text.secondary",
              fontWeight: 600,
              padding: "0.6em 1.2em",
              cursor: "pointer",
              fontSize: "0.9rem",
            }}
          >
            {t("cancel")}
          </Box>
          <Box
            component="button"
            onClick={handleSave}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              border: "none",
              borderRadius: "10px",
              backgroundColor: accent,
              color: "#fff",
              fontWeight: 700,
              padding: "0.6em 1.4em",
              cursor: "pointer",
              fontSize: "0.9rem",
              boxShadow: `0 6px 16px ${accent}4d`,
              transition: "transform .15s ease",
              "&:hover": { transform: "translateY(-1px)" },
            }}
          >
            <CheckIcon fontSize="small" />
            {t("save")}
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SignaturePad;
